import React, { useState } from "react";
import {
  Mountain,
  MapPin,
  Shield,
  Compass,
  Route,
  Zap,
  Droplets,
  TreePine,
  Tractor,
  Home,
  Heart,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Sidebar from "../components/Sidebar";
import Banner from "../components/Banner";
import Reveal from "../components/Reveal";
import mountains from "../assets/images/image15.jpg";
import animal1 from "../assets/images/image14.jpg";
import article from "../assets/images/image16.jpg";
import magnusRiffle from "../assets/images/image11.png";

const About = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { t } = useTranslation();

  const stats = [
    { value: "4", label: t("about.presence.stats.concessions"), Icon: Compass },
    {
      value: "500,000",
      label: t("about.presence.stats.hectares"),
      Icon: MapPin,
    },
  ];

  return (
    <div>
      <Header onOpenSidebar={() => setSidebarOpen(true)} />
      <Banner
        image={mountains}
        title={t("about.bannerTitle")}
        subtitle={t("about.bannerSubtitle")}
        desc={t("about.bannerDesc")}
        onOpenSidebar={() => setSidebarOpen(true)}
      />
      <section className="py-16 sm:py-24 px-4 text-center text-[#223746] max-w-4xl mx-auto">
        <Reveal
          as="p"
          direction="up"
          className="text-sm sm:text-base uppercase tracking-[0.3em] text-yellow-600 mb-4"
        >
          {t("about.intro.kicker")}
        </Reveal>
        <Reveal
          as="h2"
          direction="up"
          delay={0.15}
          className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6"
        >
          {t("about.intro.title")}
        </Reveal>
        <Reveal
          as="p"
          direction="up"
          delay={0.3}
          className="text-base sm:text-lg text-[#223746]/80"
        >
          {t("about.intro.tagline")}
        </Reveal>
      </section>
      <section className="grid grid-cols-1 lg:grid-cols-5 bg-[#f4f1ec]">
        <Reveal direction="left" className="lg:col-span-2 flex items-center">
          <div className="p-6 sm:p-10 lg:p-16">
            <div className="flex items-center gap-3 mb-4">
              <Shield size={20} className="text-yellow-600" />
              <span className="text-sm uppercase tracking-[0.3em] text-yellow-600">
                {t("about.origin.kicker")}
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#223746] mb-6">
              {t("about.origin.title")}
            </h2>
            <p className="text-[#223746] text-sm sm:text-base mb-4">
              {t("about.origin.paragraph1")}
            </p>
            <p className="text-[#223746] text-sm sm:text-base mb-4">
              {t("about.origin.paragraph2")}
            </p>
            <p className="text-[#223746] text-sm sm:text-base">
              {t("about.origin.paragraph3")}
            </p>
          </div>
        </Reveal>
        <Reveal
          direction="scale"
          delay={0.15}
          className="lg:col-span-3 w-full h-[280px] sm:h-[420px] lg:h-[800px]"
        >
          <img
            src={animal1}
            alt="Origin"
            className="w-full h-full object-cover"
          />
        </Reveal>
      </section>
      <section className="grid grid-cols-1 lg:grid-cols-5">
        <Reveal
          direction="scale"
          delay={0.15}
          className="lg:col-span-3 lg:order-1 order-1 w-full h-[280px] sm:h-[420px] lg:h-[800px]"
        >
          <img
            src={article}
            alt="Philosophy"
            className="w-full h-full object-fill"
          />
        </Reveal>
        <Reveal
          direction="right"
          delay={0.15}
          className="lg:col-span-2 lg:order-2 flex items-center"
        >
          <div className="p-6 sm:p-10 lg:p-16">
            <div className="flex items-center gap-3 mb-4">
              <Mountain size={20} className="text-yellow-600" />
              <span className="text-sm uppercase tracking-[0.3em] text-yellow-600">
                {t("about.philosophy.kicker")}
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#223746] mb-6">
              {t("about.philosophy.title")}
            </h2>
            <p className="text-[#223746] text-sm sm:text-base mb-4">
              {t("about.philosophy.paragraph1")}
            </p>
            <p className="text-[#223746] text-sm sm:text-base font-semibold border-l-2 border-yellow-600 pl-4 mt-6">
              {t("about.philosophy.highlight")}
            </p>
          </div>
        </Reveal>
      </section>
      <section className="bg-[#223746] text-white py-16 sm:py-24 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <Reveal
            as="p"
            direction="up"
            className="text-sm sm:text-base uppercase tracking-[0.3em] text-yellow-500 mb-4"
          >
            {t("about.presence.kicker")}
          </Reveal>
          <Reveal
            as="h2"
            direction="up"
            delay={0.15}
            className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6"
          >
            {t("about.presence.title")}
          </Reveal>
          <Reveal
            as="p"
            direction="up"
            delay={0.3}
            className="text-sm sm:text-base text-gray-300 max-w-3xl mx-auto mb-12"
          >
            {t("about.presence.body")}
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mt-12">
            {stats.map(({ value, label, Icon }, idx) => (
              <Reveal
                key={label}
                direction="up"
                delay={idx * 0.15}
                className="flex flex-col items-center border-t border-white/10 pt-8"
              >
                <Icon size={32} className="text-yellow-500 mb-4" />
                <p className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2">
                  {value}
                </p>
                <p className="text-xs sm:text-sm uppercase tracking-widest text-gray-300">
                  {label}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      {/* Community Impact Section */}
      <section className="bg-[#f4f1ec] py-16 sm:py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Reveal
              as="p"
              direction="up"
              className="text-sm sm:text-base uppercase tracking-[0.3em] text-yellow-600 mb-4"
            >
              {t("about.community.kicker")}
            </Reveal>
            <Reveal
              as="h2"
              direction="up"
              delay={0.15}
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#223746] mb-6"
            >
              {t("about.community.title")}
            </Reveal>
            <Reveal
              as="p"
              direction="up"
              delay={0.3}
              className="text-sm sm:text-base text-[#223746]/80 max-w-3xl mx-auto"
            >
              {t("about.community.intro")}
            </Reveal>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Infrastructure */}
            <Reveal
              direction="up"
              delay={0}
              className="bg-white rounded-2xl p-8 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-yellow-600/10 flex items-center justify-center">
                  <Shield size={20} className="text-yellow-600" />
                </div>
                <h3 className="text-lg font-bold text-[#223746]">
                  {t("about.community.infrastructure.title")}
                </h3>
              </div>
              <ul className="space-y-5">
                <li className="flex gap-3">
                  <Route
                    size={18}
                    className="text-yellow-600 mt-1 shrink-0"
                  />
                  <p className="text-sm text-[#223746]/80">
                    {t("about.community.infrastructure.road")}
                  </p>
                </li>
                <li className="flex gap-3">
                  <Zap size={18} className="text-yellow-600 mt-1 shrink-0" />
                  <p className="text-sm text-[#223746]/80">
                    {t("about.community.infrastructure.power")}
                  </p>
                </li>
                <li className="flex gap-3">
                  <Droplets
                    size={18}
                    className="text-yellow-600 mt-1 shrink-0"
                  />
                  <p className="text-sm text-[#223746]/80">
                    {t("about.community.infrastructure.water")}
                  </p>
                </li>
              </ul>
            </Reveal>

            {/* Conservation */}
            <Reveal
              direction="up"
              delay={0.15}
              className="bg-white rounded-2xl p-8 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-yellow-600/10 flex items-center justify-center">
                  <TreePine size={20} className="text-yellow-600" />
                </div>
                <h3 className="text-lg font-bold text-[#223746]">
                  {t("about.community.conservation.title")}
                </h3>
              </div>
              <ul className="space-y-5">
                <li className="flex gap-3">
                  <Mountain
                    size={18}
                    className="text-yellow-600 mt-1 shrink-0"
                  />
                  <p className="text-sm text-[#223746]/80">
                    {t("about.community.conservation.camp")}
                  </p>
                </li>
                <li className="flex gap-3">
                  <Tractor
                    size={18}
                    className="text-yellow-600 mt-1 shrink-0"
                  />
                  <p className="text-sm text-[#223746]/80">
                    {t("about.community.conservation.farms")}
                  </p>
                </li>
                <li className="flex gap-3">
                  <Home size={18} className="text-yellow-600 mt-1 shrink-0" />
                  <p className="text-sm text-[#223746]/80">
                    {t("about.community.conservation.lodge")}
                  </p>
                </li>
              </ul>
            </Reveal>

            {/* Welfare */}
            <Reveal
              direction="up"
              delay={0.3}
              className="bg-white rounded-2xl p-8 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-yellow-600/10 flex items-center justify-center">
                  <Heart size={20} className="text-yellow-600" />
                </div>
                <h3 className="text-lg font-bold text-[#223746]">
                  {t("about.community.welfare.title")}
                </h3>
              </div>
              <ul className="space-y-5">
                <li className="flex gap-3">
                  <Heart
                    size={18}
                    className="text-yellow-600 mt-1 shrink-0"
                  />
                  <p className="text-sm text-[#223746]/80">
                    {t("about.community.welfare.aid")}
                  </p>
                </li>
              </ul>
            </Reveal>
          </div>
        </div>
      </section>
      <Footer />
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
    </div>
  );
};

export default About;
